/**
 * MeContext - fetches /api/platform/me on mount and exposes the
 * resulting user / client info to the dashboard tree.
 *
 * Used by the Sidebar (brand, user card, client switcher) and the
 * Rail (theme toggle is theme-only; everything else with user
 * context goes through here).
 *
 * On 401 the user is unauthenticated - this typically means the
 * session expired while the SPA was idle. We send them back to
 * /login via a full-page nav (so the Flask redirect chain works
 * end-to-end). Other errors surface as null `me` with `error`
 * set; callers render whatever fallback makes sense.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe, type MeResponse } from '../api/platform';
import { ApiError } from '../api/client';

interface MeContextValue {
  me: MeResponse | null;
  isLoading: boolean;
  error: Error | null;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ['platform-me'],
    queryFn: getMe,
    retry: (failureCount, err) => {
      // Don't retry on 401 - bouncing through Flask's auth redirect
      // is faster than waiting for two more failed retries.
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60_000,
  });

  // 401 → kick back to /login with the current URL as `next` so the
  // user lands back here after signing in.
  if (
    query.error instanceof ApiError &&
    query.error.status === 401 &&
    typeof window !== 'undefined'
  ) {
    const next = encodeURIComponent(window.location.pathname);
    window.location.assign(`/login?next=${next}`);
  }

  const value: MeContextValue = {
    me: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
  };

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error('useMe must be used inside <MeProvider>');
  }
  return ctx;
}
